"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _react = require("react");
/**
 * Returns a unique ID using React 18's `useId` under-the-hood.
 *
 * @example
 * ```
 * const id = useUniqueId() // a unique id created with React's `useId` (e.g. ":r0:")
 * // or
 * const id = useUniqueId({ prefix: "my-prefix-" }) // previx + a unique id created with React's `useId` (e.g. "my-prefix-:r1:")
 * // or
 * const id = useUniqueId({ override: "override" }) // "override"
 * ```
 *
 * @description
 * __Why is this valuable?__
 *
 * From React's [`useId` documentation](https://react.dev/reference/react/useId):
 *
 * > [useUniqueId] is a React Hook for generating unique IDs that can be passed to accessibility attributes.
 *
 * __I get why `useId` is valuable. What does `useUniqueId` offer?__
 *
 * TLDR; `useUniqueId` can be passed an override or a prefix value.
 *
 * Let's say the component `<Blob />` accepts an optional `id` prop.
 *
 * ```
 * const Blob = (props) => {
 *  const { id } = props
 *
 *  return <div id={id} />
 * }
 * ```
 *
 * Now let's say we want Blob's `<div>` to _always_ have a unique ID even if one is not provided.
 *
 * Because React hooks [cannot be conditionally called](https://legacy.reactjs.org/docs/hooks-rules.html#explanation),
 * we must create a generated ID. That code could look like this:
 *
 * ```
 * const Blob = (props) => {
 *  const propId = props.id
 *  const generatedId = useId()
 *  const id = propId ?? generatedId
 *
 *  return <div id={id} />
 * }
 * ```
 *
 * `useUniqueId` simplifies all of this into one line of code where `props.id` is used if it exists, otherwise, the generated id is used.
 *
 * ```
 * const Blob = (props) => {
 *  const id = useUniqueId({ override: props.id })
 *
 *  return <div id={id} />
 * }
 * ```
 *
 */
var useUniqueId = function useUniqueId() {
  var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var override = args.override,
    _args$prefix = args.prefix,
    prefix = _args$prefix === void 0 ? "" : _args$prefix;
  var id = (0, _react.useId)();
  return override !== null && override !== void 0 ? override : prefix + id;
};
var _default = useUniqueId;
exports["default"] = _default;